// src/components/resumo/ReviewerFAB.tsx
import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, FileText, X } from 'lucide-react';
import { ActiveDraftContext } from './ReviewerContextBar';
import './ReviewerFAB.css';

interface Props {
    activeDraft: ActiveDraftContext;
    currentPageId: string;
    darkMode: boolean;
}

const PAGE_STATUS_COLORS: Record<string, string> = {
    revised:   '#16a34a',
    attention: '#d97706',
    untouched: '#9ca3af',
};

const PAGE_STATUS_LABEL: Record<string, string> = {
    revised:   'Revisada',
    attention: 'Atenção',
    untouched: 'Intocada',
};

const getInitials = (name: string) =>
    name.split(' ').filter(Boolean).map(w => w[0]).join('').substring(0, 2).toUpperCase();

const getRelativeTime = (dateStr: string): string => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1)  return 'agora';
    if (minutes < 60) return `há ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24)   return `há ${hours}h`;
    const days = Math.floor(hours / 24);
    return `há ${days} dia${days > 1 ? 's' : ''}`;
};

const ReviewerFAB: React.FC<Props> = ({ activeDraft, currentPageId, darkMode }) => {
    const [cardOpen, setCardOpen] = useState(false);
    const [popover, setPopover] = useState<'general' | 'page' | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    // Close card + popover on click outside
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                setCardOpen(false);
                setPopover(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Close popover on page change
    useEffect(() => { setPopover(null); }, [currentPageId]);

    const currentPageNote   = (activeDraft.pageNotes ?? []).find(n => n.pageId === currentPageId);
    const currentPageStatus = currentPageNote?.status ?? 'untouched';
    const hasGeneralNote    = (activeDraft.reviewNote ?? '').trim().length > 0;
    const hasPageNote       = (currentPageNote?.note   ?? '').trim().length > 0;
    const statusColor       = PAGE_STATUS_COLORS[currentPageStatus];
    const initials          = getInitials(activeDraft.authorName);

    const toggleCard = () => {
        setCardOpen(v => !v);
        setPopover(null);
    };

    const togglePopover = (which: 'general' | 'page') =>
        setPopover(p => p === which ? null : which);

    return (
        <div ref={rootRef} className="rfab-root">
            {/* ── Card ───────────────────────────────────────────── */}
            {cardOpen && (
                <div className={`rfab-card ${darkMode ? 'dark' : ''}`}>

                    {/* Popover above card */}
                    {popover === 'general' && hasGeneralNote && (
                        <div className="rfab-popover">
                            <div className="rfab-popover-header">
                                <span className="rfab-popover-title">
                                    <MessageSquare size={13} /> Nota geral
                                </span>
                                <button className="rfab-popover-close" onClick={() => setPopover(null)}>
                                    <X size={13} />
                                </button>
                            </div>
                            <div className="rfab-popover-body">{activeDraft.reviewNote}</div>
                        </div>
                    )}

                    {popover === 'page' && hasPageNote && (
                        <div className="rfab-popover">
                            <div className="rfab-popover-header">
                                <span className="rfab-popover-title" style={{ color: statusColor }}>
                                    <FileText size={13} /> {PAGE_STATUS_LABEL[currentPageStatus]}
                                </span>
                                <button className="rfab-popover-close" onClick={() => setPopover(null)}>
                                    <X size={13} />
                                </button>
                            </div>
                            <div className="rfab-popover-body">{currentPageNote?.note}</div>
                        </div>
                    )}

                    {/* Author row */}
                    <div className="rfab-card-top">
                        <span className="rfab-card-avatar">{initials}</span>
                        <div className="rfab-card-info">
                            <span className="rfab-card-name">{activeDraft.authorName}</span>
                            <span className="rfab-card-time">{getRelativeTime(activeDraft.updatedAt)}</span>
                        </div>
                        <span
                            className="rfab-card-status"
                            style={{ color: statusColor }}
                        >
                            {PAGE_STATUS_LABEL[currentPageStatus]}
                        </span>
                    </div>

                    <div className="rfab-card-divider" />

                    {/* Note buttons */}
                    <div className="rfab-card-actions">
                        <button
                            className={`rfab-action-btn ${popover === 'general' ? 'active' : ''}`}
                            disabled={!hasGeneralNote}
                            onClick={() => togglePopover('general')}
                            title={hasGeneralNote ? 'Ver nota geral' : 'Sem nota geral'}
                        >
                            <MessageSquare size={14} />
                            <span>Nota geral</span>
                        </button>
                        <button
                            className={`rfab-action-btn ${popover === 'page' ? 'active' : ''}`}
                            disabled={!hasPageNote}
                            onClick={() => togglePopover('page')}
                            title={hasPageNote ? 'Ver nota desta página' : 'Sem nota para esta página'}
                        >
                            <FileText size={14} />
                            <span>Esta página</span>
                        </button>
                    </div>
                </div>
            )}

            {/* ── FAB button ─────────────────────────────────────── */}
            <button
                className={`rfab-btn ${cardOpen ? 'open' : ''}`}
                onClick={toggleCard}
                title={`Rascunho de ${activeDraft.authorName}`}
            >
                <span className="rfab-initials">{initials}</span>
                <span
                    className="rfab-status-dot"
                    style={{ background: statusColor }}
                />
            </button>
        </div>
    );
};

export default ReviewerFAB;
